import { apiFetch } from "@hub/ui/api";
import { useEffect, useState } from "react";

interface Bucket {
  at: string;
  beats: number;
  cpuPercent: number | null;
  memUsedMb: number | null;
  memTotalMb: number | null;
  diskUsedGb: number | null;
  diskTotalGb: number | null;
  load1: number | null;
}
interface History {
  bucketMinutes: number;
  buckets: Bucket[];
  uptime: number;
  outages: { from: string; to: string; minutes: number }[];
}

const RANGES = [
  { label: "24h", hours: 24, bucket: 5 },
  { label: "7d", hours: 168, bucket: 60 },
] as const;

/**
 * An inline sparkline. Gaps are drawn as gaps, never interpolated across —
 * a line running straight through an outage is the single most misleading
 * thing an uptime chart can do, and the reason the API returns every bucket in
 * the window rather than only the populated ones.
 */
function Spark({ values, max, label }: { values: (number | null)[]; max: number; label: string }) {
  const w = 240;
  const h = 32;
  const step = values.length > 1 ? w / (values.length - 1) : w;
  // Each contiguous run of real readings becomes its own path, plus the matching
  // closed path for the area fill. The scale stays ABSOLUTE (CPU against 100,
  // load against 4) — auto-scaling to the observed maximum would draw an idle
  // box and a pegged one identically, which is the opposite of what an operator
  // needs. The fill is what stops a genuinely low reading from looking like a
  // stray rule along the bottom edge.
  const runs: { line: string; area: string }[] = [];
  let line: string[] = [];
  let firstX = 0;
  let lastX = 0;
  const flush = () => {
    if (line.length > 1) {
      runs.push({
        line: line.join(" "),
        area: `${line.join(" ")} L${lastX.toFixed(1)},${h} L${firstX.toFixed(1)},${h} Z`,
      });
    }
    line = [];
  };
  values.forEach((v, i) => {
    if (v === null) {
      flush();
      return;
    }
    const x = i * step;
    const y = h - Math.min(1, v / (max || 1)) * (h - 2) - 1;
    if (line.length === 0) firstX = x;
    lastX = x;
    line.push(`${line.length === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`);
  });
  flush();

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="h-8 w-full text-primary"
      role="img"
      aria-label={label}
      preserveAspectRatio="none"
    >
      <title>{label}</title>
      {runs.map((r) => (
        <g key={r.line.slice(0, 24)}>
          <path d={r.area} fill="currentColor" fillOpacity="0.14" stroke="none" />
          <path d={r.line} fill="none" stroke="currentColor" strokeWidth="1.5" />
        </g>
      ))}
    </svg>
  );
}

/** Reachability over the window: one cell per bucket, red where no heartbeat
 *  arrived. Deliberately not averaged into a single percentage — "97% uptime"
 *  hides whether that was one long outage or constant flapping. */
function UptimeStrip({ buckets }: { buckets: Bucket[] }) {
  return (
    <div className="flex h-6 w-full gap-px overflow-hidden rounded">
      {buckets.map((b) => (
        <div
          key={b.at}
          className={`flex-1 ${b.beats > 0 ? "bg-[var(--success)]/70" : "bg-destructive"}`}
          title={`${new Date(b.at).toLocaleString()} — ${b.beats > 0 ? `${b.beats} beats` : "no heartbeat"}`}
        />
      ))}
    </div>
  );
}

export function InstanceHistory({ id }: { id: string }) {
  const [range, setRange] = useState<(typeof RANGES)[number]>(RANGES[0]);
  const [history, setHistory] = useState<History | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHistory(null);
    apiFetch<History>(
      `/api/instances/${id}/history?hours=${range.hours}&bucketMinutes=${range.bucket}`,
    )
      .then(setHistory)
      .catch(() => setError("Could not load history."));
  }, [id, range]);

  if (error) return <p className="text-sm text-muted-foreground">{error}</p>;

  return (
    <section className="mb-8 rounded-xl border border-border bg-card p-5">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          History
        </h2>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.label}
              type="button"
              onClick={() => setRange(r)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                r.label === range.label
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!history ? (
        <div className="h-32" aria-hidden="true" />
      ) : history.buckets.length === 0 ? (
        <p className="text-sm text-muted-foreground">No history yet for this instance.</p>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">Reachability</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {(history.uptime * 100).toFixed(1)}% of {history.buckets.length} buckets
                {history.outages.length > 0
                  ? ` · ${history.outages.length} outage${history.outages.length === 1 ? "" : "s"}`
                  : ""}
              </span>
            </div>
            <UptimeStrip buckets={history.buckets} />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Metric
              title="CPU"
              now={lastOf(history.buckets, (b) => b.cpuPercent)}
              peak={maxOf(history.buckets, (b) => b.cpuPercent)}
              unit="%"
              spark={
                <Spark
                  values={history.buckets.map((b) => b.cpuPercent)}
                  max={100}
                  label="CPU over time"
                />
              }
            />
            <Metric
              title="Memory"
              now={lastOf(history.buckets, (b) => b.memUsedMb)}
              peak={maxOf(history.buckets, (b) => b.memUsedMb)}
              format={formatMb}
              unit={` / ${formatMb(lastOf(history.buckets, (b) => b.memTotalMb))} MB`}
              spark={
                <Spark
                  values={history.buckets.map((b) => b.memUsedMb)}
                  max={lastOf(history.buckets, (b) => b.memTotalMb) ?? 1}
                  label="Memory over time"
                />
              }
            />
            <Metric
              title="Load (1m)"
              now={lastOf(history.buckets, (b) => b.load1)}
              peak={maxOf(history.buckets, (b) => b.load1)}
              unit=""
              spark={
                <Spark
                  values={history.buckets.map((b) => b.load1)}
                  max={4}
                  label="Load over time"
                />
              }
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Disk {formatGb(lastOf(history.buckets, (b) => b.diskUsedGb))} /{" "}
            {formatGb(lastOf(history.buckets, (b) => b.diskTotalGb))} GB · {history.bucketMinutes}
            -minute buckets
          </p>
        </div>
      )}
    </section>
  );
}

function Metric({
  title,
  now,
  peak,
  unit,
  spark,
  format = round1,
}: {
  title: string;
  now: number | null;
  peak: number | null;
  unit: string;
  spark: React.ReactNode;
  format?: (v: number) => string;
}) {
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2.5">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs text-muted-foreground">{title}</span>
        <span className="text-xs font-medium tabular-nums">
          {now === null ? "—" : format(now)}
          {now === null ? "" : unit}
        </span>
      </div>
      {spark}
      {/* The chart is drawn on an absolute scale, so a quiet box is a line near
          the floor. The peak is what tells an operator whether it stayed
          quiet — without it the chart alone cannot distinguish "idle all day"
          from "idle now, pegged an hour ago". */}
      <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
        {peak === null ? "no readings" : `peak ${format(peak)}${unit}`}
      </p>
    </div>
  );
}

/**
 * Round a metric for display.
 *
 * Every value here is a SQL `avg()` over a bucket, so it arrives as a
 * full-precision double — CPU read `1.6200399855373822%` and disk
 * `31.794444444444444 GB` on the page before this existed. One decimal is the
 * most any of these readings can honestly support.
 */
function round1(v: number): string {
  return v.toFixed(1);
}

function formatGb(v: number | null): string {
  return v === null ? "?" : v.toFixed(1);
}

/** Megabytes are large enough that a decimal is noise — whole MB reads better
 *  and keeps "1400 / 3900 MB" from becoming "1398.7 / 3900.0 MB". */
function formatMb(v: number | null): string {
  return v === null ? "?" : Math.round(v).toLocaleString();
}

/** Highest non-null reading in the window. */
function maxOf(buckets: Bucket[], pick: (b: Bucket) => number | null): number | null {
  let best: number | null = null;
  for (const b of buckets) {
    const v = pick(b);
    if (v !== null && (best === null || v > best)) best = v;
  }
  return best;
}

/** Most recent non-null reading, so a trailing empty bucket does not blank the
 *  current value. */
function lastOf(buckets: Bucket[], pick: (b: Bucket) => number | null): number | null {
  for (let i = buckets.length - 1; i >= 0; i--) {
    const b = buckets[i];
    if (!b) continue;
    const v = pick(b);
    if (v !== null) return v;
  }
  return null;
}
